/**
 * Idle Clicker Game
 * Click to earn points, buy upgrades and auto-clickers
 */

import React, { useState, useEffect } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const IdleClicker = ({ onHome }) => {
  // Game State
  const [gameState, setGameState] = useState(GAME_CONFIG.states.PLAYING);
  const [points, setPoints] = useLocalStorage('idleClicker_points', 0);
  const [totalClicks, setTotalClicks] = useLocalStorage('idleClicker_totalClicks', 0);

  // Upgrades State
  const [clickValue, setClickValue] = useLocalStorage('idleClicker_clickValue', 1);
  const [autoClickersCount, setAutoClickersCount] = useLocalStorage('idleClicker_autoClickers', 0);
  const [workersCount, setWorkersCount] = useLocalStorage('idleClicker_workers', 0);

  // Upgrade Costs (scale dynamically)
  const getUpgradeCost = (currentLevel, baseCost) => {
    return Math.floor(baseCost * Math.pow(1.15, currentLevel));
  };

  const clickUpgradeCost = getUpgradeCost(clickValue - 1, 10);
  const autoClickerCost = getUpgradeCost(autoClickersCount, 50);
  const workerCost = getUpgradeCost(workersCount, 100);

  const autoClickerRate = autoClickersCount * 0.5; // 0.5 clicks per second per auto-clicker
  const workerRate = workersCount * 1.0; // 1 click per second per worker

  // Auto-clickers effect
  useEffect(() => {
    if (autoClickersCount === 0 && workersCount === 0) return;
    if (gameState !== GAME_CONFIG.states.PLAYING) return;

    const totalRate = autoClickerRate + workerRate;
    const interval = setInterval(() => {
      setPoints(prev => prev + totalRate);
    }, 1000);

    return () => clearInterval(interval);
  }, [autoClickersCount, workersCount, gameState, autoClickerRate, workerRate]);

  // Handlers
  const handleClick = () => {
    setPoints(prev => prev + clickValue);
    setTotalClicks(prev => prev + 1);
  };

  const handleUpgradeClick = () => {
    if (points >= clickUpgradeCost) {
      setPoints(prev => prev - clickUpgradeCost);
      setClickValue(prev => prev + 1);
    }
  };

  const handleBuyAutoClicker = () => {
    if (points >= autoClickerCost) {
      setPoints(prev => prev - autoClickerCost);
      setAutoClickersCount(prev => prev + 1);
    }
  };

  const handleBuyWorker = () => {
    if (points >= workerCost) {
      setPoints(prev => prev - workerCost);
      setWorkersCount(prev => prev + 1);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure? This will reset your progress!')) {
      setPoints(0);
      setTotalClicks(0);
      setClickValue(1);
      setAutoClickersCount(0);
      setWorkersCount(0);
    }
  };

  const totalPassiveIncome = autoClickerRate + workerRate;

  const controlsContent = (
    <div style={styles.upgradesContainer}>
      {/* Click Upgrade */}
      <div style={styles.upgradeBox}>
        <div style={styles.upgradeTitle}>💎 Click Upgrade</div>
        <div style={styles.upgradeValue}>{clickValue} points/click</div>
        <button
          className="btn btn-sm btn-info w-100 mt-2"
          onClick={handleUpgradeClick}
          disabled={points < clickUpgradeCost || gameState !== GAME_CONFIG.states.PLAYING}
        >
          Cost: {clickUpgradeCost.toFixed(0)}
        </button>
      </div>

      {/* Auto-Clicker */}
      <div style={styles.upgradeBox}>
        <div style={styles.upgradeTitle}>🤖 Auto-Clicker</div>
        <div style={styles.upgradeValue}>{autoClickersCount} units</div>
        <div style={styles.upgradeRate}>+{autoClickerRate.toFixed(1)}/sec</div>
        <button
          className="btn btn-sm btn-warning w-100 mt-2"
          onClick={handleBuyAutoClicker}
          disabled={points < autoClickerCost || gameState !== GAME_CONFIG.states.PLAYING}
        >
          Cost: {autoClickerCost.toFixed(0)}
        </button>
      </div>

      {/* Worker */}
      <div style={styles.upgradeBox}>
        <div style={styles.upgradeTitle}>👷 Worker</div>
        <div style={styles.upgradeValue}>{workersCount} workers</div>
        <div style={styles.upgradeRate}>+{workerRate.toFixed(1)}/sec</div>
        <button
          className="btn btn-sm btn-success w-100 mt-2"
          onClick={handleBuyWorker}
          disabled={points < workerCost || gameState !== GAME_CONFIG.states.PLAYING}
        >
          Cost: {workerCost.toFixed(0)}
        </button>
      </div>

      {/* Total Income */}
      <div style={styles.upgradeBox}>
        <div style={styles.upgradeTitle}>💰 Passive Income</div>
        <div style={styles.upgradeValue}>{totalPassiveIncome.toFixed(1)}</div>
        <div style={styles.upgradeRate}>points/second</div>
      </div>
    </div>
  );

  return (
    <GameLayout
      title="Idle Clicker ⭐"
      score={Math.floor(points)}
      gameState={gameState}
      controlsContent={controlsContent}
      onHome={onHome}
      showControls={true}
    >
      <div style={styles.gameArea}>
        {/* Main Clicker Button */}
        <button
          onClick={handleClick}
          disabled={gameState !== GAME_CONFIG.states.PLAYING}
          style={styles.clickButton}
          className="btn btn-primary btn-lg"
          aria-label="Click to earn points"
        >
          <div style={styles.clickIcon}>⭐</div>
          <div style={styles.clickText}>CLICK!</div>
          <div style={styles.clickValue}>+{clickValue}</div>
        </button>

        {/* Stats */}
        <div style={styles.statsArea}>
          <ScoreDisplay
            score={Math.floor(points)}
            stats={{
              'Total Clicks': totalClicks,
              'Click Value': clickValue,
              'Passive /sec': totalPassiveIncome.toFixed(1),
            }}
          />
        </div>

        {/* Reset Button */}
        <button
          onClick={handleReset}
          className="btn btn-danger btn-sm mt-3"
          aria-label="Reset game"
        >
          🔄 Reset Progress
        </button>
      </div>

      <style>{`
        button:hover:not(:disabled) {
          transform: scale(1.05);
        }

        button:active:not(:disabled) {
          transform: scale(0.95);
        }
      `}</style>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2rem',
    width: '100%',
  },
  clickButton: {
    width: '150px',
    height: '150px',
    borderRadius: '50%',
    fontSize: '2.5rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    fontWeight: 'bold',
    transition: `all ${GAME_CONFIG.transitions.fast}`,
    border: `3px solid ${GAME_CONFIG.colors.accent}`,
    boxShadow: `0 0 20px ${GAME_CONFIG.colors.primary}`,
  },
  clickIcon: {
    fontSize: '3rem',
  },
  clickText: {
    fontSize: '0.9rem',
    color: GAME_CONFIG.colors.accent2,
  },
  clickValue: {
    fontSize: '0.75rem',
    color: GAME_CONFIG.colors.accent,
  },
  statsArea: {
    width: '100%',
    maxWidth: '600px',
  },
  upgradesContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '1rem',
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto',
  },
  upgradeBox: {
    backgroundColor: `rgba(0, 0, 0, 0.3)`,
    border: `1px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '1rem',
    textAlign: 'center',
  },
  upgradeTitle: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.9rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
    textTransform: 'uppercase',
  },
  upgradeValue: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '1.25rem',
    fontWeight: 'bold',
  },
  upgradeRate: {
    color: GAME_CONFIG.colors.accent2,
    fontSize: '0.8rem',
    marginTop: '0.25rem',
  },
};

export default IdleClicker;
